import { createTheme } from '@mui/material'
import type { PaletteMode, Theme } from '@mui/material/styles'
import type { Localization } from '@mui/material/locale'
import DBScreenHead from './fonts/DBScreenHead-Black.woff2'
import DBScreenSans from './fonts/DBScreenSans-Regular.woff2'
import { textFieldTheme } from './themeComponents/textFieldTheme'
import { buttonTheme } from './themeComponents/buttonTheme'
import { selectTheme } from './themeComponents/selectTheme'
import { dialogTheme } from './themeComponents/dialogTheme'
import { alertTheme } from './themeComponents/alertTheme'
import { dataGridTheme } from './themeComponents/dataGridTheme'
import { chipTheme } from './themeComponents/chipTheme'
import { semanticColors, colorTokens, cssVariableDeclarations } from './tokens/colorTokens'
import { typographyTheme } from './tokens/typographyTokens'

const sizing: Theme['sizing'] = {
    control: {
        compact: '1.5rem',
        regular: '2rem',
    },
}

export default function theme(mode: PaletteMode, language?: Localization, brandColors = semanticColors) {
    return createTheme(
        {
            sizing,
            palette: {
                mode: mode,
                primary: {
                    main: brandColors.brand.primary,
                    contrastText: brandColors.brand.primaryContrastText,
                },
                dark: {
                    main: brandColors.surface.dark,
                    dark: brandColors.surface.dark,
                    contrastText: brandColors.text.contrast,
                },
                light: {
                    main: brandColors.surface.dark,
                    dark: brandColors.surface.dark,
                    contrastText: brandColors.text.contrast,
                },
                secondary: {
                    main: brandColors.brand.secondary,
                    dark: brandColors.brand.secondaryHover,
                    contrastText: brandColors.brand.secondaryContrastText,
                },
                white: {
                    main: brandColors.surface.default,
                },
                info: {
                    main: brandColors.status.info,
                },
                grey: {
                    main: brandColors.text.secondary,
                },
                text: {
                    primary: brandColors.text.primary,
                    secondary: brandColors.text.secondary,
                    disabled: brandColors.text.disabled,
                },
                background: {
                    default: brandColors.background.default,
                    paper: brandColors.surface.default,
                },
            },

            root: {
                // input label when focused
                '& label.Mui-focused': {
                    color: colorTokens.border.focus,
                },
                // focused color for input with variant='standard'
                '& .MuiInput-underline:after': {
                    borderBottomColor: colorTokens.border.focus,
                },
                // focused color for input with variant='filled'
                '& .MuiFilledInput-underline:after': {
                    borderBottomColor: colorTokens.border.focus,
                },
                // focused color for input with variant='outlined'
                '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                        borderColor: colorTokens.border.focus,
                    },
                },
                '& .MuiTableCell-root': {
                    color: 'red',
                },
            },

            typography: typographyTheme,

            components: {
                ...textFieldTheme,
                ...buttonTheme,
                ...selectTheme,
                ...dialogTheme,
                ...alertTheme,
                ...dataGridTheme,
                ...chipTheme,
                MuiLink: {
                    styleOverrides: {
                        root: ({ theme: t }) => ({
                            color: `${t.palette.primary.dark} !important`,
                            textDecoration: 'underline !important',
                            '&:visited': {
                                color: `${t.palette.primary.dark} !important`,
                            },
                            '&:hover': {
                                color: `${t.palette.primary.main} !important`,
                            },
                        }),
                    },
                },
                MuiCssBaseline: {
                    styleOverrides: `
          ${cssVariableDeclarations}
          @font-face {
            font-family: 'DB Head Black';
            font-style: normal;
            font-weight: bold;
            src: local('Raleway'), local('Raleway-Regular'), url(${DBScreenHead}) format('woff2');
           
          }
          @font-face {
            font-family: 'DB Sans Regular';
            font-weight: normal;
            font-style: normal;
            src: local('Raleway'), local('Raleway-Regular'), url(${DBScreenSans}) format('woff2');
          }
        `,
                },
            },
        },
        language ?? {}
    )
}

export function darkTheme(brandColors = semanticColors) {
    return createTheme({
        sizing,
        palette: {
            mode: 'dark',
            primary: { main: brandColors.surface.default },
            background: {
                default: brandColors.background.dark,
                color: brandColors.surface.default,
            },
        },
    })
}